import React from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  CheckCircle, 
  CreditCard, 
  FolderOpen, 
  BookOpen, 
  Calendar, 
  ClipboardList,
  ChevronRight,
  User,
  Users
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useClassWizard } from './ClassWizardContext';
import { Badge } from '@/components/ui/badge';

interface ClassWizardStepsProps {
  currentStep: number;
}

const steps = [
  {
    id: 1,
    name: 'Cliente',
    path: '/class-wizard/step1',
    icon: User,
    description: 'Selecione um cliente'
  },
  {
    id: 2,
    name: 'Assinatura',
    path: '/class-wizard/step2',
    icon: CreditCard,
    description: 'Selecione uma assinatura ativa'
  },
  {
    id: 3,
    name: 'Solicitantes',
    path: '/class-wizard/step3',
    icon: Users,
    description: 'Adicione contatos solicitantes'
  },
  {
    id: 4,
    name: 'Família de Cursos',
    path: '/class-wizard/step4',
    icon: FolderOpen,
    description: 'Escolha uma família de cursos'
  },
  {
    id: 5,
    name: 'Curso',
    path: '/class-wizard/step5',
    icon: BookOpen,
    description: 'Selecione um curso específico'
  },
  {
    id: 6,
    name: 'Sessões',
    path: '/class-wizard/step6',
    icon: Calendar,
    description: 'Defina as datas das sessões'
  },
  {
    id: 7,
    name: 'Resumo',
    path: '/class-wizard/step7',
    icon: ClipboardList,
    description: 'Revise os detalhes da classe'
  },
];

const ClassWizardSteps: React.FC<ClassWizardStepsProps> = ({ currentStep }) => {
  const navigate = useNavigate();
  const { state } = useClassWizard();

  const canNavigateToStep = (stepId: number) => {
    if (stepId === 1) return true;
    if (stepId === 2) return !!state.selectedCustomer;
    if (stepId === 3) return !!state.selectedSubscription;
    if (stepId === 4) return !!state.selectedSubscription && state.selectedSponsors.length > 0;
    if (stepId === 5) return !!state.selectedCourseFamily;
    if (stepId === 6) return !!state.selectedCourse;
    if (stepId === 7) return state.sessions.length > 0;
    return false;
  };

  const handleStepClick = (step: { id: number; path: string }) => {
    if (canNavigateToStep(step.id) && step.id < currentStep) {
      navigate(step.path);
    }
  };

  // Mobile view - simplified steps
  const mobileStepper = (
    <div className="block md:hidden mb-6">
      <div className="flex items-center justify-between">
        <Badge variant="outline" className="px-3 py-1 text-xs">
          Passo {currentStep} de {steps.length}
        </Badge>
        <div className="text-sm font-extrabold text-primary">
          {steps[currentStep - 1]?.name}
        </div>
      </div>
      <div className="w-full bg-muted h-1.5 mt-3 rounded-full overflow-hidden">
        <div 
          className="bg-primary h-full rounded-full transition-all duration-300"
          style={{ width: `${(currentStep / steps.length) * 100}%` }}
        />
      </div>
    </div>
  );

  // Desktop view - full steps
  const desktopStepper = (
    <div className="hidden md:block overflow-x-auto pb-4 pt-4">
      <div className="min-w-full flex items-start">
        {steps.map((step, index) => {
          const StepIcon = step.icon;
          const isActive = step.id === currentStep;
          const isCompleted = step.id < currentStep;
          const isClickable = isCompleted && canNavigateToStep(step.id);
          
          return (
            <div key={step.id} className="flex-1">
              <div 
                className={cn(
                  "flex-1 relative",
                  isClickable ? "cursor-pointer group" : ""
                )}
                onClick={isClickable ? () => handleStepClick(step) : undefined}
              >
                <div className="flex flex-col items-center min-h-[80px] pt-2">
                  <div className={cn(
                    "w-12 h-12 rounded-full flex items-center justify-center z-10 mb-3 transition-all",
                    isActive 
                      ? "bg-primary text-white ring-4 ring-primary/20" 
                      : isCompleted 
                        ? "bg-primary text-white" 
                        : "bg-muted text-muted-foreground"
                  )}>
                    {isCompleted ? (
                      <CheckCircle className="h-6 w-6" />
                    ) : (
                      <StepIcon className="h-6 w-6" />
                    )}
                  </div>
                  
                  <div className="text-center space-y-1 px-1">
                    <p className={cn(
                      "text-sm font-extrabold leading-tight",
                      isActive 
                        ? "text-primary" 
                        : isCompleted 
                          ? "text-foreground" 
                          : "text-muted-foreground"
                    )}>
                      {step.name}
                    </p>
                    <p className="text-xs text-muted-foreground hidden lg:block leading-tight">
                      {step.description}
                    </p>
                  </div>
                </div>
                
                {index < steps.length - 1 && (
                  <div className="absolute top-8 -right-3 z-0 transform -translate-y-1/2 text-muted-foreground">
                    <ChevronRight className="h-5 w-5" />
                  </div>
                )}
                
                {index < steps.length - 1 && (
                  <div 
                    className={cn(
                      "absolute top-8 left-12 right-5 h-[2px] transform -translate-y-1/2",
                      isCompleted ? "bg-primary" : "bg-muted"
                    )}
                  />
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );

  return (
    <div>
      {mobileStepper}
      {desktopStepper}
    </div>
  );
};

export default ClassWizardSteps;